/*
 * Copyright (c) 2024. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import type { EnvInfo } from '@poweralpha/common';
import { upperCase } from 'lodash';
import { useMemo } from 'react';
import { SalesforceIcon } from './react-components/salesforce/SalesforceLinkIcon';

const PROD_ENVS = ['demo', 'prod'];

interface Props {
  compact?: boolean;
  envInfo: EnvInfo | undefined;
}

function EnvLabel({ compact = false, envInfo }: Readonly<Props>) {
  const envLabel = useMemo(
    () =>
      envInfo?.env && (!PROD_ENVS.includes(envInfo.env) || process.env.NODE_ENV === 'development')
        ? upperCase(envInfo.env)
        : null,
    [envInfo],
  );

  if (!envLabel) {
    return null;
  }

  const sfUser = envInfo?.sfUsername?.split('@')[0];
  const sfDomain = envInfo?.sfUsername?.split('@')[1].split('.').at(-1);

  if (compact) {
    // Rose glass chip from the v4 mockups (`.envchip`); the Salesforce user moves to the tooltip.
    return (
      <Tooltip
        title={sfUser ? `Salesforce: ${sfUser}${sfDomain === 'com' ? '' : ` (${sfDomain})`}` : ''}
      >
        <Box
          data-testid="env-chip"
          sx={(theme) => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            height: 26,
            px: '10px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.6px',
            whiteSpace: 'nowrap',
            color: theme.palette.mode === 'dark' ? '#FDA4AF' : '#BE123C',
            backgroundColor: 'rgba(251, 113, 133, 0.12)',
            border: '1px solid rgba(251, 113, 133, 0.25)',
            backdropFilter: 'blur(14px)',
          })}
        >
          <Box
            component="span"
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: '#FB7185',
              boxShadow: '0 0 0 3px rgba(251, 113, 133, 0.18)',
            }}
          />
          {envLabel}
          {process.env.NODE_ENV === 'development' && (
            <Box component="span" sx={{ fontWeight: 500, opacity: 0.7 }}>
              · local
            </Box>
          )}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Stack>
      <Typography
        sx={{
          color: 'error.main',
          fontWeight: 'fontWeightBold',
          ml: 2,
          fontSize: { sm: 16, xs: 8 },
        }}
      >
        {envLabel}
        <i>{process.env.NODE_ENV === 'development' ? ' (local)' : ''}</i>
      </Typography>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          ml: 2,
        }}
      >
        <SalesforceIcon height={16} />
        <Typography
          sx={{
            color: 'info.main',
            fontSize: { sm: 12, xs: 8 },
          }}
        >
          {sfUser === 'administration_test' ? '' : sfUser}
          <i>{sfDomain === 'com' ? '' : `(${sfDomain})`}</i>
        </Typography>
        {sfDomain === 'com' && <WarningAmberIcon color="warning" fontSize="small" />}
      </Stack>
    </Stack>
  );
}

export default EnvLabel;
